import React, { useCallback, useEffect, useMemo, useState } from 'react';
// icons
import { useLocation, LinkProps, Link, NavLink } from 'react-router-dom';

import AampeTooltip from 'components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';
import ICONS from '../../constants/icons';

import { createShouldForwardProp } from 'helpers/shouldForwardProp';

// components
import BetaMode from '../auth/BetaMode';
import { SideNavItem } from './SideNavItem.type';

const shouldForwardProps = createShouldForwardProp([
    'isActive',
    'collapsed',
    'isAnyChildActive',
    'open',
]);

interface SideNavButtonProps {
    item: SideNavItem;
    subItems?: SideNavItem[];
    collapsed: boolean;
}

interface ChildNavigateProps {
    isActive: boolean;
    hasWarning?: boolean;
}
interface NavigateProps extends ChildNavigateProps {
    collapsed: boolean;
    isAnyChildActive: boolean;
}

interface PopoverContainerStyleProps {
    open: boolean;
}

interface LinkStyleProps extends LinkProps {
    isActive: boolean;
}
interface HeaderProps {
    isActive: boolean;
}

const SideNavButton: React.FC<React.PropsWithChildren<SideNavButtonProps>> = (
    props,
) => {
    const { item, subItems, collapsed } = props;
    const { destination, icon, text, webLink } = item;
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [openPopup, setOpenPopup] = useState<HTMLElement | null>(null);
    const location = useLocation();
    const open = Boolean(openPopup);
    const { pathname } = location;
    const toggleSubMenu = () => setIsExpanded((expanded) => !expanded);

    const isItemActive = useMemo(() => {
        return pathname.includes(destination); // Example
    }, [pathname, destination]);

    const isChildActive = useCallback(
        (subItemLocation: string) => pathname.includes(subItemLocation),
        [pathname],
    );

    const isAnyChildActive = useMemo(
        () =>
            !!subItems?.some((subItem: { destination: string }) =>
                isChildActive(subItem.destination),
            ),
        [subItems, isChildActive],
    );

    const renderArrowIcon = useMemo(() => {
        const handleArrowClick = () => {
            toggleSubMenu();
        };

        if (subItems?.length) {
            if (isExpanded) {
                return (
                    <ToggleSubmenu onClick={handleArrowClick}>
                        <StyledFiChevronUp size={20} />
                    </ToggleSubmenu>
                );
            }
            return (
                <ToggleSubmenu onClick={handleArrowClick}>
                    <StyledFiChevronDown size={20} />
                </ToggleSubmenu>
            );
        }
    }, [subItems, isExpanded]);

    const renderSubItems = useMemo(() => {
        if (subItems?.length && isExpanded && !collapsed) {
            return subItems.map((item: SideNavItem) => {
                const pathName = item.destination.split('?')[0];
                const isActive = isChildActive(pathName);
                const element = (
                    <ChildNavigate
                        isActive={isActive}
                        hasWarning={item.hasWarning}
                        key={item.destination}
                    >
                        <AampeTooltip
                            arrow
                            placement={'right'}
                            title={
                                item.warningTooltip
                                    ? item.warningTooltip
                                    : undefined
                            }
                        >
                            {item.webLink ? (
                                // eslint-disable-next-line react/jsx-no-target-blank
                                <a
                                    href={item.webLink}
                                    target={!item.nxLink ? '_blank' : undefined}
                                    rel={
                                        !item.nxLink ? 'noreferrer' : undefined
                                    }
                                >
                                    {item.icon ? (
                                        <SubIconContainer>
                                            <SubItemPopoverText>
                                                {item.text}
                                            </SubItemPopoverText>
                                            {item.icon}
                                        </SubIconContainer>
                                    ) : (
                                        item.text
                                    )}
                                </a>
                            ) : (
                                <NavLink to={item.destination}>
                                    {item.text}
                                </NavLink>
                            )}
                        </AampeTooltip>
                    </ChildNavigate>
                );

                if (item.isBetaMode) {
                    return (
                        <BetaMode key={item.destination}>{element}</BetaMode>
                    );
                }
                return element;
            });
        }
    }, [subItems, isExpanded, isChildActive, collapsed]);

    const handlePopoverOpen = (event: any) => {
        setOpenPopup(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setOpenPopup(null);
    };

    useEffect(() => {
        if (!isItemActive) {
            setIsExpanded(false);
        } else {
            setIsExpanded(true);
        }
    }, [isItemActive]);

    useEffect(() => {
        if (collapsed) {
            setIsExpanded(false);
        }
    }, [collapsed]);

    return (
        <Container>
            <Navigate
                collapsed={collapsed}
                isActive={isItemActive}
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
                // onClick={handleParentItemClick}
                isAnyChildActive={isAnyChildActive}
            >
                <ItemContainer>
                    <IconPlusText>
                        {icon && (
                            <>
                                <IconContainer isActive={isItemActive}>
                                    <NavLink to={destination}>{icon}</NavLink>
                                </IconContainer>
                                {collapsed && (
                                    <PopoverContainer
                                        open={open}
                                        onClick={(event) =>
                                            event.stopPropagation()
                                        }
                                    >
                                        <PopoverContent>
                                            <NavLink to={destination}>
                                                <PopoverHeader
                                                    isActive={isItemActive}
                                                >
                                                    {text}
                                                </PopoverHeader>
                                            </NavLink>
                                            {subItems?.map((subItem) => {
                                                return (
                                                    <LabelContainer
                                                        key={
                                                            subItem.destination
                                                        }
                                                    >
                                                        {subItem.webLink ? (
                                                            // eslint-disable-next-line react/jsx-no-target-blank
                                                            <a
                                                                href={
                                                                    subItem.webLink
                                                                }
                                                                target={
                                                                    !item.nxLink
                                                                        ? '_blank'
                                                                        : undefined
                                                                }
                                                                rel={
                                                                    !item.nxLink
                                                                        ? 'noreferrer'
                                                                        : undefined
                                                                }
                                                            >
                                                                {subItem.icon ? (
                                                                    <SubIconContainer>
                                                                        <SubItemPopoverText>
                                                                            {
                                                                                subItem.text
                                                                            }
                                                                        </SubItemPopoverText>
                                                                        {
                                                                            subItem.icon
                                                                        }
                                                                    </SubIconContainer>
                                                                ) : (
                                                                    subItem.text
                                                                )}
                                                            </a>
                                                        ) : subItem.icon ? (
                                                            <SubIconContainer>
                                                                <SubItemPopoverText>
                                                                    {
                                                                        subItem.text
                                                                    }
                                                                </SubItemPopoverText>
                                                                {subItem.icon}
                                                            </SubIconContainer>
                                                        ) : (
                                                            <StyledLink
                                                                className={
                                                                    isChildActive(
                                                                        subItem.destination,
                                                                    )
                                                                        ? 'active'
                                                                        : 'inactive'
                                                                }
                                                                to={
                                                                    subItem.destination
                                                                }
                                                                isActive={isChildActive(
                                                                    subItem.destination,
                                                                )}
                                                            >
                                                                {subItem.text}
                                                            </StyledLink>
                                                        )}
                                                    </LabelContainer>
                                                );
                                            })}
                                        </PopoverContent>
                                    </PopoverContainer>
                                )}
                            </>
                        )}
                        {!collapsed &&
                            (webLink ? (
                                // eslint-disable-next-line react/jsx-no-target-blank
                                <a
                                    href={webLink}
                                    target={!item.nxLink ? '_blank' : undefined}
                                    rel={
                                        !item.nxLink ? 'noreferrer' : undefined
                                    }
                                >
                                    {text}
                                </a>
                            ) : (
                                <NavLink to={destination}>{text}</NavLink>
                            ))}
                    </IconPlusText>
                </ItemContainer>
                {!collapsed && renderArrowIcon}
            </Navigate>
            {renderSubItems}
        </Container>
    );
};

const Container = styled.div({
    overflow: 'visible',
    width: '100%',
});

const IconPlusText = styled.div({
    gap: aampeTheme.spacing4(2),
    display: 'flex',
    flexDirection: 'row',
    height: aampeTheme.size.lineHeight.small,
});
const Navigate = styled('div').withConfig({
    shouldForwardProp: shouldForwardProps,
})<NavigateProps>`
    display: flex;
    justify-content: ${(props: NavigateProps) =>
        props.collapsed ? 'center' : 'space-between'};
    background-color: ${(props: NavigateProps) =>
        props.isActive && (!props.isAnyChildActive || props.collapsed)
            ? aampeTheme.colors.backgroundWG02
            : aampeTheme.colors.white};
    margin-left: ${aampeTheme.spacing(2)};
    padding: ${aampeTheme.spacing(0.5, 1)};
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
    justify-content: ${(props: NavigateProps) =>
        props.collapsed ? 'flex-start' : 'space-between'};
    position: relative;
    justify-content: ${(props: NavigateProps) =>
        props.collapsed ? 'flex-start' : 'space-between'};
    cursor: pointer;

    a {
        min-width: 24px;
        line-height: ${aampeTheme.size.lineHeight.small};
        display: flex;
        align-items: center;
        font-size: ${aampeTheme.size.font.textMedium};
        font-weight: ${(props: NavigateProps) =>
            props.isActive
                ? aampeTheme.weights.medium
                : aampeTheme.weights.light};
        text-decoration: none;
        &:hover {
            color: ${(props: NavigateProps) =>
                props.isActive
                    ? aampeTheme.colors.solidA02
                    : aampeTheme.colors.textAccessibleWG01};
        }
        color: ${(props: NavigateProps) =>
            props.isActive
                ? aampeTheme.colors.solidA01
                : aampeTheme.colors.textAccessibleWG01};
    }
`;

const ChildNavigate = styled('div').withConfig({
    shouldForwardProp: shouldForwardProps,
})<ChildNavigateProps>`
    display: block;
    padding: ${aampeTheme.spacing4(1.5, 2)};
    margin-left: ${aampeTheme.spacing(6)};
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
    background-color: ${({ isActive }) =>
        isActive ? aampeTheme.colors.backgroundWG02 : aampeTheme.colors.white};

    line-height: ${aampeTheme.size.lineHeight.small};
    position: relative;

    a {
        display: inline;
        cursor: pointer;
        text-transform: none;
        font-size: ${aampeTheme.size.font.textMedium};
        text-decoration-line: underline;
        text-underline-offset: 2px;

        color: ${({ hasWarning, isActive }) =>
            hasWarning
                ? aampeTheme.colors.orange09
                : isActive
                ? aampeTheme.colors.solidA01
                : aampeTheme.colors.textAccessibleWG01};

        text-decoration-color: ${({ hasWarning }) =>
            hasWarning ? aampeTheme.colors.orange09 : 'transparent'};
        text-decoration-style: dotted;
        text-decoration-thickness: 1px;

        &:hover {
            color: ${({ hasWarning, isActive }) =>
                hasWarning
                    ? aampeTheme.colors.orange10
                    : isActive
                    ? aampeTheme.colors.solidA02
                    : aampeTheme.colors.textAccessibleWG01};

            text-decoration-color: ${({ hasWarning }) =>
                hasWarning ? aampeTheme.colors.orange10 : 'transparent'};
        }
    }
`;

const ItemContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    height: aampeTheme.spacing4(6),
    width: '100%',
});

const ToggleSubmenu = styled.div({
    display: 'flex',
    alignItems: 'center',
    margin: aampeTheme.spacing(0, 1, 0, 0),
});

const IconContainer = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<ChildNavigateProps>(({ isActive }) => ({
    color: isActive
        ? aampeTheme.colors.solidA01
        : aampeTheme.colors.textAccessibleWG01,
    margin: 0,
    stroke: isActive
        ? aampeTheme.colors.solidA01
        : aampeTheme.colors.textAccessibleWG01,
}));

const PopoverContainer = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<PopoverContainerStyleProps>(({ open }) => ({
    '--popover-gap': '4px',
    display: open ? 'flex' : 'none',
    minWidth: '120px',
    width: 'max-content',
    left: 'calc(100% + var(--popover-gap))',
    position: 'absolute',
    zIndex: 1001,
    alignContent: 'center',
    borderRadius: aampeTheme.borderRadius.default,
    '&::before': {
        content: '""',
        position: 'absolute',
        right: '100%',
        width: 'calc(var(--popover-gap) + 10px)',
        height: '100%',
        background: 'transparent',
    },
}));

const PopoverContent = styled.div({
    borderRadius: aampeTheme.borderRadius.default,
    padding: aampeTheme.spacing(1),
    background: aampeTheme.colors.white,
    width: '100%',

    boxShadow:
        '0 10px 25px -5px rgb(0 0 0 / 0.35), 0 8px 10px -6px rgb(0 0 0 / 0.35)',
});

const PopoverHeader = styled('div')<HeaderProps>`
    width: 100%;
    display: flex;
    padding: ${aampeTheme.spacing4(1, 0, 1, 2)};
    color: ${(props: HeaderProps) =>
        props.isActive
            ? aampeTheme.colors.solidA01
            : aampeTheme.colors.textAccessibleWG01};

    font-weight: ${aampeTheme.weights.medium};
    font-size: ${aampeTheme.size.font.textSmall};
   text-transform: uppercase;
   letter-spacing: 0.5px;
   margin-bottom: ${aampeTheme.spacing4(1)}

     &:hover {
        color: ${(props: HeaderProps) =>
            props.isActive
                ? aampeTheme.colors.solidA02 // Different color for active hover
                : aampeTheme.colors
                      .textAccessibleWG01}; // Hover color for inactive items
`;

const StyledLink = styled(Link)<LinkStyleProps>`
    text-decoration: none;

    &.inactive {
        line-height: ${aampeTheme.size.lineHeight.small};
        font-size: ${aampeTheme.size.font.textMedium};
        background-color: ${aampeTheme.colors.white};
        color: ${aampeTheme.colors.textAccessibleWG01};
        font-weight: ${aampeTheme.weights.light};
        &:hover {
            color: ${({ isActive }) =>
                isActive
                    ? aampeTheme.colors.solidA02
                    : aampeTheme.colors.textAccessibleWG01};
        }
    }
`;
const LabelContainer = styled.div({
    display: 'flex',
    padding: aampeTheme.spacing4(1, 0, 1, 2),
    alignContent: 'center',
    flexDirection: 'column',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
});

const StyledFiChevronUp = styled(ICONS.chevronUp)({
    color: aampeTheme.colors.textAccessibleWG01,
    '&:hover': {
        color: aampeTheme.colors.textAccessibleWG01,
    },
});

const StyledFiChevronDown = styled(ICONS.chevronDown)({
    color: aampeTheme.colors.textAccessibleWG01,
    '&:hover': {
        color: aampeTheme.colors.textAccessibleWG01,
    },
});
const SubIconContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    alignItems: 'baseline',
});
const SubItemPopoverText = styled.div({
    width: '105px',
});

export default SideNavButton;
